import React, { useState } from 'react';
import JSZip from 'jszip';
import Modal from './Modal.mjs';
import ClosingX from './ClosingX.jsx';
import BButton from './BButton.mjs';
import ProgressBar from './common/ProgressBar.jsx';
import InlineBanner from './common/InlineBanner.jsx';
import * as FileService from '../services/FileService.mjs';
import { runBulk } from '../utils/bulkApply';
import { reportError } from '../notifications/errorBus';
import '../css/BulkActions.css';

// At or below this count the files download one-by-one; above it they're
// bundled into a single .zip so the browser isn't hit with dozens of saves.
const INDIVIDUAL_LIMIT = 10;
const SKIP = { skipGlobalErrorToast: true };

const nameOf = (f) => f.metadata?.name || f.id;

// Avoid clobbering same-named files inside the zip by suffixing " (n)".
function uniqueName(used, name) {
    if (!used.has(name)) { used.add(name); return name; }
    const dot = name.lastIndexOf('.');
    const base = dot > 0 ? name.slice(0, dot) : name;
    const ext = dot > 0 ? name.slice(dot) : '';
    let i = 1;
    let candidate;
    do { candidate = `${base} (${i})${ext}`; i += 1; } while (used.has(candidate));
    used.add(candidate);
    return candidate;
}

const BulkDownloadModal = ({ files, onClose }) => {
    const isZip = files.length > INDIVIDUAL_LIMIT;
    const [phase, setPhase] = useState('confirm'); // 'confirm' | 'working' | 'done'
    const [done, setDone] = useState(0);
    const [zipPct, setZipPct] = useState(null);
    const [result, setResult] = useState({ succeeded: 0, failed: 0 });

    const total = files.length;
    const pct = isZip && zipPct != null
        ? Math.round(zipPct)
        : total ? Math.round((done / total) * 100) : 0;

    const start = async () => {
        setPhase('working');
        try {
            if (isZip) {
                const zip = new JSZip();
                const used = new Set();
                const { succeeded, failed } = await runBulk(
                    files,
                    async (f) => {
                        const blob = await FileService.fetchFileBlob(f.id, SKIP);
                        zip.file(uniqueName(used, nameOf(f)), blob);
                    },
                    (d) => setDone(d)
                );
                if (succeeded.length > 0) {
                    const content = await zip.generateAsync(
                        { type: 'blob' },
                        (meta) => setZipPct(meta.percent)
                    );
                    FileService.saveBlob(content, `qdex-download-${succeeded.length}-files.zip`);
                }
                setResult({ succeeded: succeeded.length, failed: failed.length });
            } else {
                const { succeeded, failed } = await runBulk(
                    files,
                    async (f) => {
                        const blob = await FileService.fetchFileBlob(f.id, SKIP);
                        FileService.saveBlob(blob, nameOf(f));
                    },
                    (d) => setDone(d)
                );
                setResult({ succeeded: succeeded.length, failed: failed.length });
            }
        } catch (err) {
            reportError(err, { title: 'Download failed' });
        } finally {
            setPhase('done');
        }
    };

    return (
        <Modal onClose={onClose}>
            <div className="bulk-modal">
                <div className="bulk-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h2>Download {total} file(s)</h2>
                        <p>
                            {isZip
                                ? `More than ${INDIVIDUAL_LIMIT} files — these will be bundled into a single .zip.`
                                : 'Each file downloads individually.'}
                        </p>
                    </div>
                    <ClosingX onClick={onClose} />
                </div>

                {phase === 'confirm' && (
                    <div className="bulk-footer">
                        <BButton onClick={onClose}>Cancel</BButton>
                        <BButton className="btn-primary" onClick={start}>
                            {isZip ? 'Download .zip' : 'Download'}
                        </BButton>
                    </div>
                )}

                {phase === 'working' && (
                    <ProgressBar
                        value={pct}
                        showPercent
                        label={
                            isZip && done >= total
                                ? 'Compressing…'
                                : `Preparing ${done} / ${total}…`
                        }
                    />
                )}

                {phase === 'done' && (
                    <>
                        {result.succeeded > 0 && (
                            <InlineBanner variant="success">
                                {isZip ? 'Zipped' : 'Downloaded'} {result.succeeded} file(s).
                            </InlineBanner>
                        )}
                        {result.failed > 0 && (
                            <InlineBanner variant="danger">
                                {result.failed} file(s) could not be downloaded.
                            </InlineBanner>
                        )}
                        {result.succeeded === 0 && result.failed === 0 && (
                            <InlineBanner variant="info">Nothing to download.</InlineBanner>
                        )}
                        <div className="bulk-footer">
                            <BButton className="btn-primary" onClick={onClose}>Done</BButton>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default BulkDownloadModal;
